import { Locale, Locales } from '@schema';
import { defineStore } from 'pinia';
import { I18N_COOKIE_KEY, I18N_DEFAULT_LOCALE } from '@/common/constants/i18n.constants';
import i18n from '@/common/i18n';
import { applyDayJsLocale, applyUikitLocale, loadLocaleMessages } from '@/common/utils/loadLocales';
import { get as getCookie, remove as removeCookie, set as setCookie } from 'tiny-cookie';
import extendLangList from '@/common/utils/extendLangList';
import api from '@/common/utils/api';

export const useI18nStore = defineStore('i18n', {
  state: () => ({
    languages: [
      {
        language: 'en',
        display_name: 'English',
        english_name: 'English',
        enabled: true,
        is_default: true
      }
    ] as Locale[]
  }),
  getters: {
    getDefaultLocale: state => state.languages.find(l => l.is_default)?.language || I18N_DEFAULT_LOCALE,
    getEnabledLanguages: state => state.languages.filter(l => l.enabled),
    isLanguageChangeAvailable(): boolean {
      return this.getEnabledLanguages.length > 1;
    }
  },
  actions: {
    setLocale(locale) {
      i18n.locale = locale;

      applyDayJsLocale(locale);
      setCookie(I18N_COOKIE_KEY[APP_BUILD], locale, { expires: '10Y', secure: true, samesite: 'None' });
      document.querySelector('html')?.setAttribute('lang', locale);
    },
    async loadElementUIMessages(locale: string) {
      await applyUikitLocale(locale);
    },
    loadMessages(locale: string) {
      return loadLocaleMessages(locale);
    },
    loadLanguageList() {
      return api.get<Locales>('/api/v2/settings/locales')
              .then(res => {
                if (process.env.NODE_ENV === 'test') {
                  this.languages = extendLangList(res.data.data);
                } else {
                  this.languages = res.data.data;
                }
              })
              .catch(() => {
                  // The error is muted because it shouldn't break application run
                console.error('Cannot load language list');
              });
    },
    async loadLanguage(localeName: string|undefined = undefined, onlyAllowed = false) {
      let locale: string;
      const cookieLocale = getCookie(I18N_COOKIE_KEY[APP_BUILD]);
      if (localeName) {
        locale = localeName;
      } else if (cookieLocale) {
        locale = cookieLocale;
      } else {
        locale = this.getDefaultLocale;
      }

      const allowedLanguages = onlyAllowed ? this.getEnabledLanguages : this.languages;

      if (!allowedLanguages.find(l => l.language === locale)) {
        locale = this.getDefaultLocale;
        removeCookie(I18N_COOKIE_KEY[APP_BUILD]);
      }

      if (i18n.locale !== locale || !cookieLocale) {
        await Promise.all([
          this.loadElementUIMessages(locale),
          this.loadMessages(locale)
        ]).finally(() => this.setLocale(locale));
      }
      return locale;
    }
  }
});

export default useI18nStore;
